/**
 * W5 — the shop's OPEN unattended operations (machine processes running on vehicles), keyed by ticket id.
 *
 * Read ONCE at the Dashboard level and threaded to every TechnicianColumn (the same lift pattern as
 * advisorBayHoldByTicketId), so N columns cost one query. Kept current by the realtime publication of
 * ticket_unattended_operations (W5 migration) plus a refetch on the board's `ticket-updated` event.
 *
 * Display data only: the server decides everything about the operations (start_/end_unattended_operation).
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { OpenUnattendedOperation } from '@/lib/dayboard/multiBayActivity';

export type UnattendedByTicketId = Map<string, OpenUnattendedOperation[]>;

const EMPTY: UnattendedByTicketId = new Map();

export function groupOpenUnattendedByTicket(rows: readonly OpenUnattendedOperation[]): UnattendedByTicketId {
  const m: UnattendedByTicketId = new Map();
  for (const r of rows) {
    const list = m.get(r.ticket_id);
    if (list) list.push(r);
    else m.set(r.ticket_id, [r]);
  }
  for (const list of m.values()) list.sort((a, b) => a.started_at.localeCompare(b.started_at));
  return m;
}

export function useOpenUnattendedOperations(shopId: string | null | undefined): UnattendedByTicketId {
  const [byTicket, setByTicket] = useState<UnattendedByTicketId>(EMPTY);

  useEffect(() => {
    if (!shopId) {
      setByTicket(EMPTY);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const { data, error } = await supabase
        .from('ticket_unattended_operations' as never)
        .select('id, ticket_id, kind, kind_note, started_at, expected_minutes, started_by_technician_id, bay_id_at_start')
        .eq('shop_id' as never, shopId as never)
        .is('ended_at' as never, null as never);
      if (cancelled) return;
      if (error) {
        // Unknown is not "none": keep the last known state rather than inventing an empty board.
        console.warn('[useOpenUnattendedOperations] read failed:', error.message);
        return;
      }
      setByTicket(groupOpenUnattendedByTicket((data ?? []) as unknown as OpenUnattendedOperation[]));
    };
    void load();

    const channel = supabase
      .channel(`w5-unattended-${shopId}`)
      .on(
        'postgres_changes' as never,
        { event: '*', schema: 'public', table: 'ticket_unattended_operations', filter: `shop_id=eq.${shopId}` } as never,
        () => { void load(); },
      )
      .subscribe();
    const onTicketUpdated = () => { void load(); };
    window.addEventListener('ticket-updated', onTicketUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener('ticket-updated', onTicketUpdated);
      void supabase.removeChannel(channel);
    };
  }, [shopId]);

  return byTicket;
}
